import { List } from "@/components/list/List";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { MonthYearTableFilter } from "@/components/data-table/components/MonthYearTableFilter";
import { Module, PATH_CONTEST_AGREEMENT } from "@/constants";
import {
  contestAgreementList,
  contestAgreementListRestricted,
} from "@/schemas/contestAgreement";
import type { ContestAgreementRead } from "@/types/contestAgreement";
import { fetchContestAgreementsList } from "@/services/contestAgreements";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { SuccessFetchListResponse } from "@/services/api";
import { useCallback, useMemo } from "react";
import { generatePath } from "react-router-dom";
import { useContestAgreementsFieldPermissions } from "../hooks/useContestAgreementsFieldPermissions";
import { useContestAgreementsColumnOverrides } from "../hooks/useContestAgreementsColumnOverrides";

const ContestAgreementsList = () => {
  const options = useCreateDataTableOptions<ContestAgreementRead>(
    Module.ContestAgreements,
  );

  const queryKey = useDynamicQueryParams(Module.ContestAgreements, options);
  const { canSeePresentationImageCount } =
    useContestAgreementsFieldPermissions();

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ContestAgreementRead>) =>
      lastPage.last_item,
    [],
  );

  const fetchList = useCallback(
    async ({ pageParam }: { pageParam?: string }) =>
      fetchContestAgreementsList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [options.filters, options.sorting],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: fetchList,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  const columnOverrides = useContestAgreementsColumnOverrides();

  return (
    <DataTableQueryContextProvider value={queryValue}>
      <List<ContestAgreementRead>
        schema={
          canSeePresentationImageCount
            ? contestAgreementList
            : contestAgreementListRestricted
        }
        name={Module.ContestAgreements}
        translationKey="contestAgreements"
        query={query}
        options={options}
        tableFeatures={{
          exportSettings: { moduleName: Module.ContestAgreements },
          initialColumnVisibility: {
            agreement2_presentation_image_count: canSeePresentationImageCount,
          },
        }}
        pathBuilder={(row) =>
          generatePath(PATH_CONTEST_AGREEMENT, { id: row.id })
        }
        columnOverrides={columnOverrides}
      >
        <List.Header breadcrumbLabel="contestAgreements.title.multiple" />
        <div className="flex flex-row gap-2 justify-between items-center py-2">
          <List.Menubar className="mb-0 grow">
            <List.Menubar.Tools />
            <List.Menubar.Columns />
            <List.Menubar.Reset />
          </List.Menubar>

          <MonthYearTableFilter
            columnId="period"
            className="shrink-0 grow-0"
            popoverClassName="z-100"
            disabled={query.isFetching}
          />
        </div>
        <List.Content>
          <List.Status />
        </List.Content>
      </List>
    </DataTableQueryContextProvider>
  );
};

export default ContestAgreementsList;
